import React, { useState, useEffect } from "react";
import { useShow, useUpdate, useSelect } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Tabs, Form, Input, Select, Card, Space, Button, Row, Col, Tag, Divider, Typography, Alert, Spin, message, Table, Switch, InputNumber, DatePicker, Checkbox, Tooltip } from "antd";
import { InfoCircleOutlined, SaveOutlined, MailOutlined, EditOutlined, PictureOutlined, SettingOutlined, DollarOutlined, EnvironmentOutlined, CheckSquareOutlined, StarOutlined, HomeOutlined, PlusOutlined, DeleteOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { IAdvert } from "../../interfaces";
import { useParams, useNavigate } from "react-router-dom";
import { PricingTab } from "./pricing/PricingTab";
import { FileBrowserWizard } from "../../components/FileBrowserWizard";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const AdvertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  
  // Daten aus der API/JSON laden
  const { queryResult } = useShow<IAdvert>({
    resource: "adverts",
    id: id,
    dataProviderName: "default"
  });

  const { mutate: updateAdvert, isLoading: isUpdating } = useUpdate<IAdvert>();

  const advert = queryResult?.data?.data;
  const loading = queryResult?.isLoading;

  useEffect(() => {
    if (advert) {
      console.log('🔧 Setting form values for advert:', advert); // Debug
      console.log('🔧 arrival_departure_rules from advert:', advert.arrival_departure_rules); // Debug
      
      // Set form values with all advert data including arrival_departure_rules
      const formValues = {
        ...advert,
        arrival_departure_rules: advert.arrival_departure_rules || []
      };
      
      console.log('🔧 Setting form values:', formValues); // Debug
      form.setFieldsValue(formValues);
    }
  }, [advert, form]);

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      updateAdvert({
        resource: "adverts",
        id: id!,
        values,
      }, {
        onSuccess: () => {
          message.success("Änderungen gespeichert");
          setHasChanges(false);
        },
        onError: () => {
          message.error("Fehler beim Speichern");
        }
      });
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!advert) {
    return (
      <Alert
        message="Advert nicht gefunden"
        description="Das angeforderte Inserat konnte nicht gefunden werden."
        type="error"
        showIcon
      />
    );
  }

  return (
    <Show
      title={`Inserat: ${advert.title || "Ohne Titel"}`}
      headerButtons={[
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isUpdating}
          disabled={!hasChanges}
        >
          Speichern
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
      >
        <Tabs defaultActiveKey="1">
          {/* Tab 1: Stammdaten */}
          <TabPane 
            tab={
              <span>
                <EditOutlined />
                Stammdaten
              </span>
            } 
            key="1"
                    >
            <StammdatenTab advert={advert} form={form} />
          </TabPane>

          {/* Tab 2: Objektbeschreibung */}
          <TabPane 
            tab={
              <span>
                <InfoCircleOutlined />
                Objektbeschreibung
              </span>
            } 
            key="2"
          >
                          <ObjektbeschreibungTab />
          </TabPane>

          {/* Tab 3: Bilder */}
          <TabPane 
            tab={
              <span>
                <PictureOutlined />
                Bilder
              </span>
            } 
            key="3"
          >
                            <BilderTab advert={advert} form={form} advertId={id} />
          </TabPane>

          {/* Tab 4: Ausstattung & Merkmale */}
          <TabPane 
            tab={
              <span>
                <HomeOutlined />
                Ausstattung & Merkmale
              </span>
            } 
            key="4"
          >
            <AusstattungTab advert={advert} form={form} />
          </TabPane>

          {/* Tab 5: Preise & Steuern */}
          <TabPane 
            tab={
              <span>
                <DollarOutlined />
                Preise & Steuern
              </span>
            } 
            key="5"
          >
            <PricingTab advertId={id || ''} advertData={advert} />
          </TabPane>

          {/* Tab 6: Entfernungen & Lage */}
          <TabPane 
            tab={
              <span>
                <EnvironmentOutlined />
                Entfernungen & Lage
              </span>
            } 
            key="6"
          >
            <EntfernungenTab advert={advert} form={form} />
          </TabPane>

          {/* Tab 7: Zusatzoptionen */}
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                Zusatzoptionen
              </span>
            } 
            key="7"
          >
            <ZusatzoptionenTab advert={advert} form={form} />
          </TabPane>

          {/* Hinweis auf E-Mail Templates */}
          <TabPane 
            tab={
              <span>
                <MailOutlined />
                E-Mail Vorlagen
              </span>
            } 
            key="8"
            disabled
          >
            <Alert
              message="E-Mail Vorlagen"
              description="E-Mail Vorlagen werden in einem separaten Menüpunkt verwaltet und können dort den Inseraten zugewiesen werden."
              type="info"
              showIcon
              action={
                <Button size="small" type="primary">
                  Zu E-Mail Vorlagen
                </Button>
              }
            />
          </TabPane>
        </Tabs>
      </Form>
    </Show>
  );
};

// Tab-Komponente für Stammdaten
const StammdatenTab: React.FC<{ advert: IAdvert; form: any }> = ({ advert, form }) => {
  const navigate = useNavigate();
  
  const { options: arrivalDepartureOptions, queryResult } = useSelect({
    resource: "arrival-departure-rules",
    optionLabel: "label",
    optionValue: "id",
  });

  console.log('🔧 StammdatenTab - arrivalDepartureOptions:', arrivalDepartureOptions); // Debug
  console.log('🔧 StammdatenTab - queryResult:', queryResult); // Debug
  console.log('🔧 StammdatenTab - advert.arrival_departure_rules:', advert?.arrival_departure_rules); // Debug

  const hasRules = arrivalDepartureOptions && arrivalDepartureOptions.length > 0;

  // Ensure arrival_departure_rules are set when options and advert data are available
  useEffect(() => {
    if (advert && arrivalDepartureOptions && form && advert.arrival_departure_rules) {
      console.log('🔧 StammdatenTab - Setting arrival_departure_rules:', advert.arrival_departure_rules); // Debug
      form.setFieldValue('arrival_departure_rules', advert.arrival_departure_rules);
    }
  }, [advert, arrivalDepartureOptions, form]);

  return (
    <div>
      <Title level={4}>Grundinformationen</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="title" label="Titel" rules={[{ required: true, message: "Titel ist erforderlich" }]}>
            <Input placeholder="Inserattitel" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="shorttitle" label="Kurztitel">
            <Input placeholder="Kurzer Titel" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item name="status" label="Status">
            <Select>
              <Select.Option value={1}>Aktiv</Select.Option>
              <Select.Option value={0}>Inaktiv</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={16}>
          <Form.Item 
            name="arrival_departure_rules" 
            label={
              <Space>
                <CalendarOutlined />
                An-/Abreise-Regeln (Mehrfachauswahl)
                <Tooltip title="Wählen Sie eine oder mehrere Regeln, die automatisch für dieses Objekt gelten sollen. Die Regeln bestimmen, an welchen Wochentagen und in welchen Zeiträumen Gäste an- und abreisen können.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            extra={
              hasRules 
                ? "Ohne Auswahl sind alle Wochentage für An- und Abreise erlaubt. Sie können mehrere Regeln kombinieren."
                : <Space>
                    <Text type="secondary">Keine Regeln vorhanden.</Text>
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/#/an-abreise')}
                    >
                      Regeln erstellen
                    </Button>
                  </Space>
            }
          >
            <Select
              mode="multiple"
              placeholder={hasRules ? "Regeln auswählen (optional)" : "Keine Regeln verfügbar"}
              allowClear
              showSearch
              disabled={!hasRules}
              maxTagCount="responsive"
              options={arrivalDepartureOptions}
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onDropdownVisibleChange={(open) => {
                if (open) {
                  console.log('🔧 Select opened - Current form value:', form.getFieldValue('arrival_departure_rules')); // Debug
                  console.log('🔧 Select opened - Available options:', arrivalDepartureOptions); // Debug
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Adresse</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={16}>
          <Form.Item name="street" label="Straße">
            <Input placeholder="Straßenname" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="houseNumber" label="Hausnummer">
            <Input placeholder="Nr." />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="zip" label="PLZ">
            <Input placeholder="Postleitzahl" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={16}>
          <Form.Item name="city" label="Stadt">
            <Input placeholder="Stadt" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Kapazität</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="noPersons" label="Max. Personen">
            <Input type="number" placeholder="Maximale Personenzahl" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="totalSize" label="Größe">
            <Input placeholder="z.B. 75 qm" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Firmendaten</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item name="companyName" label="Firmenname">
            <Input placeholder="Firma" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="companyOwnerName" label="Inhaber">
            <Input placeholder="Name des Inhabers" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="ustid" label="USt-IdNr.">
            <Input placeholder="DE123456789" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="advertsVat" label="MwSt-Satz">
            <Select>
              <Select.Option value="7">7%</Select.Option>
              <Select.Option value="19">19%</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Objektbeschreibung
const ObjektbeschreibungTab: React.FC = () => {
  const [language, setLanguage] = useState("de");

  return (
    <div>
      <Title level={4}>Beschreibungstexte</Title>
      
      {/* Sprachauswahl */}
      <Space style={{ marginBottom: 16 }}>
        <Text>Sprache:</Text>
        <Select value={language} onChange={setLanguage} style={{ width: 120 }}>
          <Select.Option value="de">🇩🇪 Deutsch</Select.Option>
          <Select.Option value="en">🇬🇧 Englisch</Select.Option>
          <Select.Option value="fr">🇫🇷 Französisch</Select.Option>
        </Select>
      </Space>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item 
            name={`summary_${language}`} 
            label="Kurzbeschreibung"
            extra="Kurze, prägnante Beschreibung für Übersichten"
          >
            <TextArea 
              rows={3} 
              placeholder="Gemütliche Ferienwohnung mit Meerblick..."
              maxLength={300}
              showCount
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`accommodation_${language}`} 
            label="Unterkunftsbeschreibung"
            extra="Detaillierte Beschreibung der Unterkunft"
          >
            <TextArea 
              rows={6} 
              placeholder="Ausführliche Beschreibung der Unterkunft..."
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`environment_${language}`} 
            label="Umgebung"
            extra="Beschreibung der Umgebung und Lage"
          >
            <TextArea 
              rows={4} 
              placeholder="Die Unterkunft liegt in ruhiger Lage..."
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`features_${language}`} 
            label="Ausstattung"
            extra="Besondere Merkmale und Ausstattung"
          >
            <TextArea 
              rows={4} 
              placeholder="Voll ausgestattete Küche, WLAN, Parkplatz..."
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`directions_${language}`} 
            label="Anreise / Wegbeschreibung"
          >
            <TextArea 
              rows={4} 
              placeholder="Von der Autobahn A1..."
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>SEO-Einstellungen</Title>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item 
            name={`seo_title_${language}`} 
            label="SEO-Titel"
            extra="Titel für Suchmaschinen (max. 60 Zeichen)"
          >
            <Input placeholder="Ferienwohnung Ostsee - Meerblick - 2 Schlafzimmer" maxLength={60} showCount />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`seo_description_${language}`} 
            label="SEO-Beschreibung"
            extra="Beschreibung für Suchmaschinen (max. 160 Zeichen)"
          >
            <TextArea 
              rows={2} 
              placeholder="Moderne Ferienwohnung an der Ostsee mit direktem Meerblick..."
              maxLength={160}
              showCount
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item 
            name={`seo_keywords_${language}`} 
            label="SEO-Keywords"
            extra="Kommagetrennte Schlüsselwörter"
          >
            <Input placeholder="Ferienwohnung, Ostsee, Meerblick, Strand, Urlaub" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

// Tab-Komponente für Bilder
const BilderTab: React.FC<{ advert: IAdvert; form: any; advertId?: string }> = ({ advert, form, advertId }) => {
  const [fileBrowserVisible, setFileBrowserVisible] = useState(false);
  const [currentImageCategory, setCurrentImageCategory] = useState<string>('');
  const [advertImages, setAdvertImages] = useState<{
    main: AdvertImage[];
    inside: AdvertImage[];
    outside: AdvertImage[];
    winter: AdvertImage[];
    other: AdvertImage[];
  }>({
    main: [],
    inside: [],
    outside: [],
    winter: [],
    other: []
  });
  const [loading, setLoading] = useState(true);

  // Interface für MediaFile (muss mit FileBrowserWizard kompatibel sein)
  interface MediaFile {
    uid: string;
    name: string;
    size: number;
    uploadDate: string;
    url: string;
    type: string;
  }

  // Interface für Advert-Bilder
  interface AdvertImage {
    uid: string;
    referenceUid: string;
    name: string;
    size: number;
    url: string;
    type: string;
    uploadDate: string;
    fieldname: string;
    category: string;
    sorting: number;
  }

  // Bilder laden beim Component Mount
  useEffect(() => {
    const advertIdentifier = advert?.uid || advert?.id || advertId;
    if (advert && advertIdentifier) {
      loadAdvertImages();
    }
  }, [advert, advertId]);

  const loadAdvertImages = async () => {
    const advertIdentifier = advert?.uid || advert?.id || advertId;
    if (!advert || !advertIdentifier) {
      console.error('No advert or advert identifier available');
      console.error('Advert:', advert, 'Available props:', Object.keys(advert || {}));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/rest/api?tx_buchungsmanager_frontend[action]=getAdvertImages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId: advertIdentifier
        }),
      });

              if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('Loaded advert images:', {
              total: result.total,
              categories: Object.keys(result.images).map(cat => `${cat}: ${result.images[cat].length}`),
              images: result.images
            });
            setAdvertImages(result.images);
          } else {
          console.error('Failed to load images:', result);
          message.error(`Fehler beim Laden der Bilder: ${result.error || 'Unbekannt'}`);
          
          // Debug-Informationen in Console ausgeben
          if (result.debug_message) {
            console.error('Debug Message:', result.debug_message);
            console.error('Debug Line:', result.debug_line);
          }
        }
      } else {
        const errorText = await response.text();
        console.error('HTTP error loading images:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500) // Erste 500 Zeichen
        });
        
        try {
          const errorJson = JSON.parse(errorText);
          message.error(`HTTP ${response.status}: ${errorJson.error || errorJson.debug_message || 'Unbekannter Fehler'}`);
          
          if (errorJson.debug_message) {
            console.error('Backend Debug:', errorJson.debug_message, 'Line:', errorJson.debug_line);
          }
        } catch (parseError) {
          message.error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Error loading advert images:', error);
      message.error('Fehler beim Laden der Bilder');
    } finally {
      setLoading(false);
    }
  };

  const handleAddImages = (category: string) => {
    setCurrentImageCategory(category);
    setFileBrowserVisible(true);
  };

  const handleFilesSelected = async (selectedFiles: MediaFile[]) => {
    // Prüfe ob Advert und ID verfügbar sind (unterstütze sowohl uid als auch id)
    const advertIdentifier = advert?.uid || advert?.id || advertId;
    if (!advert || !advertIdentifier) {
      message.error('Fehler: Inserat-ID nicht verfügbar');
      console.error('Advert object:', advert);
      console.error('Available properties:', Object.keys(advert || {}));
      return;
    }

    // Hier würde die API aufgerufen werden, um die Bildverknüpfungen zu speichern
    console.log(`Verknüpfe ${selectedFiles.length} Bilder mit Kategorie: ${currentImageCategory}`, selectedFiles);
    console.log('Advert ID:', advertIdentifier, 'Type:', typeof advertIdentifier);
    console.log('Using advert.uid:', advert.uid, 'advert.id:', advert.id, 'advertId param:', advertId);
    
    // Implementierung der Backend-Verknüpfung - sichere ID-Konvertierung
    const finalAdvertId = String(advertIdentifier);
    if (!finalAdvertId || finalAdvertId === 'undefined') {
      message.error('Fehler: Keine gültige Inserat-ID gefunden');
      return;
    }
    await linkImagesToAdvert(finalAdvertId, selectedFiles, currentImageCategory);
    
    // Bilder-Liste neu laden
    await loadAdvertImages();
    
    message.success(`${selectedFiles.length} Bilder erfolgreich hinzugefügt`);
  };

  const unlinkImageFromAdvert = async (advertId: string, referenceUid: string, imageName: string) => {
    try {
      console.log('Unlinking image from advert:', {
        advertId,
        referenceUid,
        imageName
      });

      const response = await fetch('/rest/api?tx_buchungsmanager_frontend[action]=unlinkMediaFromAdvert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId,
          referenceUid,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Successfully unlinked image:', result);
          message.success(`Bild "${imageName}" erfolgreich entfernt`);
          
          // Bilder-Liste neu laden
          await loadAdvertImages();
        } else {
          console.error('Unlink failed with success=false:', result);
          message.error(`Fehler: ${result.error || 'Unbekannt'}`);
        }
      } else {
        const errorText = await response.text();
        console.error('HTTP error unlinking image:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500)
        });
        
        try {
          const errorJson = JSON.parse(errorText);
          message.error(`Entfernen fehlgeschlagen: ${errorJson.error || errorJson.debug_message || 'Unbekannter Fehler'}`);
          
          if (errorJson.debug_message) {
            console.error('Backend Debug:', errorJson.debug_message, 'Line:', errorJson.debug_line);
          }
        } catch (parseError) {
          message.error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Network error unlinking image:', error);
      message.error('Netzwerk-Fehler beim Entfernen des Bildes');
    }
  };

  const linkImagesToAdvert = async (advertId: string, images: MediaFile[], category: string) => {
    try {
      console.log('Linking images to advert:', {
        advertId,
        category,
        imageCount: images.length,
        images: images.map(img => ({ uid: img.uid, name: img.name }))
      });

      const response = await fetch('/rest/api?tx_buchungsmanager_frontend[action]=linkMediaToAdvert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          advertId,
          images: images.map(img => ({ uid: img.uid, category })),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Successfully linked images:', result);
          message.success(`${result.linked_count} Bilder erfolgreich verknüpft`);
        } else {
          console.error('Link failed with success=false:', result);
          message.error(`Fehler: ${result.error || 'Unbekannt'}`);
        }
      } else {
        const errorText = await response.text();
        console.error('HTTP error linking images:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500)
        });
        
        try {
          const errorJson = JSON.parse(errorText);
          message.error(`Verknüpfung fehlgeschlagen: ${errorJson.error || errorJson.debug_message || 'Unbekannter Fehler'}`);
          
          if (errorJson.debug_message) {
            console.error('Backend Debug:', errorJson.debug_message, 'Line:', errorJson.debug_line);
          }
        } catch (parseError) {
          message.error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Network error linking images:', error);
      message.error('Netzwerk-Fehler beim Verknüpfen der Bilder');
    }
  };

  return (
    <div>
      <Title level={4}>Bildverwaltung</Title>
      
      <Alert
        message="Zentrale Bildverwaltung"
        description="Wählen Sie Bilder aus Ihrer zentralen Medienablage aus und ordnen Sie sie den entsprechenden Kategorien zu."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Hauptbild */}
      <Card 
        title="Hauptbild" 
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => handleAddImages('main')}
          >
            Hauptbild wählen
          </Button>
        }
      >
        {loading ? (
          <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
        ) : advertImages.main && advertImages.main.length > 0 ? (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img 
              src={advertImages.main[0].url} 
              alt={advertImages.main[0].name}
              style={{ maxWidth: "100%", maxHeight: 300 }}
            />
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => {
                const mainImage = advertImages.main[0];
                const advertIdentifier = advert?.uid || advert?.id || advertId;
                const finalAdvertId = String(advertIdentifier);
                unlinkImageFromAdvert(finalAdvertId, mainImage.referenceUid, mainImage.name);
              }}
            >
              Entfernen
            </Button>
          </div>
        ) : (
          <Alert 
            message="Kein Hauptbild vorhanden" 
            type="info" 
            action={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => handleAddImages('main')}
              >
                Hinzufügen
              </Button>
            }
          />
        )}
      </Card>

      {/* Innenansichten */}
      <Card 
        title="Innenansichten" 
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => handleAddImages('inside')}
          >
            Innenansichten hinzufügen
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24}>
              <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
            </Col>
          ) : advertImages.inside && advertImages.inside.length > 0 ? (
            advertImages.inside.map((img) => (
              <Col key={img.referenceUid} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={<img alt={img.name} src={img.url} style={{ height: 200, objectFit: 'cover' }} />}
                  actions={[
                    <Button
                      key="delete"
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => {
                        const advertIdentifier = advert?.uid || advert?.id || advertId;
                        const finalAdvertId = String(advertIdentifier);
                        unlinkImageFromAdvert(finalAdvertId, img.referenceUid, img.name);
                      }}
                    >
                      Entfernen
                    </Button>
                  ]}
                >
                  <Card.Meta 
                    title={img.name} 
                    description={`${(img.size / 1024).toFixed(0)} KB • ${img.uploadDate}`}
                  />
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24}>
              <Alert 
                message="Keine Innenansichten vorhanden" 
                type="info" 
                action={
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => handleAddImages('inside')}
                  >
                    Hinzufügen
                  </Button>
                }
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* Außenansichten */}
      <Card 
        title="Außenansichten" 
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => handleAddImages('outside')}
          >
            Außenansichten hinzufügen
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24}>
              <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
            </Col>
          ) : advertImages.outside && advertImages.outside.length > 0 ? (
            advertImages.outside.map((img) => (
              <Col key={img.referenceUid} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={<img alt={img.name} src={img.url} style={{ height: 200, objectFit: 'cover' }} />}
                  actions={[
                    <Button
                      key="delete"
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => {
                        const advertIdentifier = advert?.uid || advert?.id || advertId;
                        const finalAdvertId = String(advertIdentifier);
                        unlinkImageFromAdvert(finalAdvertId, img.referenceUid, img.name);
                      }}
                    >
                      Entfernen
                    </Button>
                  ]}
                >
                  <Card.Meta 
                    title={img.name} 
                    description={`${(img.size / 1024).toFixed(0)} KB • ${img.uploadDate}`}
                  />
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24}>
              <Alert 
                message="Keine Außenansichten vorhanden" 
                type="info" 
                action={
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => handleAddImages('outside')}
                  >
                    Hinzufügen
                  </Button>
                }
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* Winterbilder */}
      <Card 
        title="Winterbilder" 
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => handleAddImages('winter')}
          >
            Winterbilder hinzufügen
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24}>
              <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
            </Col>
          ) : advertImages.winter && advertImages.winter.length > 0 ? (
            advertImages.winter.map((img) => (
              <Col key={img.referenceUid} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={<img alt={img.name} src={img.url} style={{ height: 200, objectFit: 'cover' }} />}
                  actions={[
                    <Button
                      key="delete"
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => {
                        const advertIdentifier = advert?.uid || advert?.id || advertId;
                        const finalAdvertId = String(advertIdentifier);
                        unlinkImageFromAdvert(finalAdvertId, img.referenceUid, img.name);
                      }}
                    >
                      Entfernen
                    </Button>
                  ]}
                >
                  <Card.Meta 
                    title={img.name} 
                    description={`${(img.size / 1024).toFixed(0)} KB • ${img.uploadDate}`}
                  />
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24}>
              <Alert 
                message="Keine Winterbilder vorhanden" 
                type="info" 
                action={
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => handleAddImages('winter')}
                  >
                    Hinzufügen
                  </Button>
                }
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* FileBrowser Wizard */}
      <FileBrowserWizard
        visible={fileBrowserVisible}
        onClose={() => setFileBrowserVisible(false)}
        onSelect={handleFilesSelected}
        title={`Bilder für ${
          currentImageCategory === 'main' ? 'Hauptbild' :
          currentImageCategory === 'inside' ? 'Innenansichten' :
          currentImageCategory === 'outside' ? 'Außenansichten' :
          currentImageCategory === 'winter' ? 'Winterbilder' :
          'Bilder'
        } auswählen`}
        multiple={currentImageCategory !== 'main'}
        maxSelection={currentImageCategory === 'main' ? 1 : 20}
      />

      {/* Weitere Bildkategorien können hier ergänzt werden */}
    </div>
  );
};

// Tab-Komponente für Ausstattung & Merkmale
const AusstattungTab: React.FC<{ advert: IAdvert; form: any }> = ({ advert, form }) => {
  const [allEquipments, setAllEquipments] = useState<any[]>([]);
  const [allSuitableFor, setAllSuitableFor] = useState<any[]>([]);
  const [allLeisure, setAllLeisure] = useState<any[]>([]);
  const [allPlaces, setAllPlaces] = useState<any[]>([]);
  const [allAdditionalServices, setAllAdditionalServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAusstattungsOptions = async () => {
      try {
        setLoading(true);

        // Load all available options from backend
        const [equipmentsRes, suitableRes, leisureRes, placesRes, additionalServicesRes] = await Promise.all([
          fetch(`${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllEquipments`),
          fetch(`${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllSuitable`),
          fetch(`${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllLeisure`),
          fetch(`${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllPlaces`),
          fetch(`${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllAdditionalServices`)
        ]);

        const equipments = await equipmentsRes.json();
        const suitable = await suitableRes.json();
        const leisure = await leisureRes.json();
        const places = await placesRes.json();
        const additionalServices = await additionalServicesRes.json();

        setAllEquipments(equipments.data || []);
        setAllSuitableFor(suitable.data || []);
        setAllLeisure(leisure.data || []);
        setAllPlaces(places.data || []);
        setAllAdditionalServices(additionalServices.data || []);

      } catch (error) {
        console.error('Error loading ausstattung options:', error);
        message.error('Fehler beim Laden der Ausstattungsoptionen');
      } finally {
        setLoading(false);
      }
    };

    loadAusstattungsOptions();
  }, []);

  // Set initial form values from advert data
  useEffect(() => {
    if (advert && form) {
      console.log('🔧 Advert data received:', advert); // Debug
      const initialValues: any = {};
      
      // Set selected equipments
      if (advert.equipments) {
        console.log('✅ Found equipments:', advert.equipments); // Debug
        initialValues.equipments = advert.equipments.map((eq: any) => eq.uid);
      } else {
        console.log('❌ No equipments found in advert'); // Debug
      }
      
      // Set selected suitable for
      if (advert.suitableFor) {
        console.log('✅ Found suitableFor:', advert.suitableFor); // Debug
        initialValues.suitableFor = advert.suitableFor.map((sf: any) => sf.uid);
      } else {
        console.log('❌ No suitableFor found in advert'); // Debug
      }
      
      // Set selected leisure
      if (advert.leisure) {
        console.log('✅ Found leisure:', advert.leisure); // Debug
        initialValues.leisure = advert.leisure.map((l: any) => l.uid);
      } else {
        console.log('❌ No leisure found in advert'); // Debug
      }

      // Set selected additional services
      if (advert.additionalservices) {
        console.log('✅ Found additionalservices:', advert.additionalservices); // Debug
        initialValues.additionalservices = advert.additionalservices.map((as: any) => as.uid);
      } else {
        console.log('❌ No additionalservices found in advert'); // Debug
      }

      console.log('🔧 Setting form values:', initialValues); // Debug
      if (Object.keys(initialValues).length > 0) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [advert, form, allEquipments, allSuitableFor, allLeisure, allAdditionalServices]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Laden...</div>;
  }

  return (
    <div>
      <Title level={4}>Ausstattung</Title>
      <Card style={{ marginBottom: 16 }}>
        <Form.Item name="equipments" label="Verfügbare Ausstattung">
          <Checkbox.Group style={{ width: '100%' }}>
            <Row>
              {allEquipments.map((equipment) => (
                <Col key={equipment.uid} span={8} style={{ marginBottom: 8 }}>
                  <Checkbox value={equipment.uid}>
                    {equipment.title} {equipment.general && <span style={{ color: '#666' }}>(Allgemein)</span>}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </Card>

      <Title level={4}>Geeignet für</Title>
      <Card style={{ marginBottom: 16 }}>
        <Form.Item name="suitableFor" label="Diese Unterkunft ist geeignet für">
          <Select mode="multiple" placeholder="Bitte wählen Sie" style={{ width: '100%' }}>
            {allSuitableFor.map((suitable) => (
              <Select.Option key={suitable.uid} value={suitable.uid}>
                {suitable.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      <Title level={4}>Freizeitaktivitäten</Title>
      <Card style={{ marginBottom: 16 }}>
        <Form.Item name="leisure" label="Freizeitaktivitäten in der Nähe">
          <Checkbox.Group style={{ width: '100%' }}>
            <Row>
              {allLeisure.map((item) => (
                <Col key={item.uid} span={8} style={{ marginBottom: 8 }}>
                  <Checkbox value={item.uid}>
                    {item.title}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </Card>

      <Title level={4}>Räumlichkeiten</Title>
      <Card style={{ marginBottom: 16 }}>
        <Form.Item name="places" label="Verfügbare Räume">
          <Checkbox.Group style={{ width: '100%' }}>
            <Row>
              {allPlaces.map((place) => (
                <Col key={place.uid} span={8} style={{ marginBottom: 8 }}>
                  <Checkbox value={place.uid}>
                    {place.title} {place.type === 1 && <span style={{ color: '#666' }}>(Innen)</span>}
                    {place.type === 2 && <span style={{ color: '#666' }}>(Außen)</span>}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </Card>

      <Title level={4}>Zusatzleistungen</Title>
      <Card>
        <Form.Item name="additionalservices" label="Verfügbare Zusatzleistungen">
          <Checkbox.Group style={{ width: '100%' }}>
            <Row>
              {allAdditionalServices.map((service) => (
                <Col key={service.uid} span={8} style={{ marginBottom: 8 }}>
                  <Checkbox value={service.uid}>
                    {service.title}
                    {service.desc && <span style={{ color: '#666', fontSize: '12px', display: 'block' }}>({service.desc})</span>}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </Card>


    </div>
  );
};

// Tab-Komponente für Preise & Steuern
const PreiseSteuernTab: React.FC<{ advert: IAdvert; form: any }> = ({ advert, form }) => {
  return (
    <div>
      <Title level={4}>Zusatzpreise</Title>
      <Card style={{ marginBottom: 16 }}>
        <Table
          dataSource={advert.bookingadditionalprice || []}
          rowKey="uid"
          pagination={false}
          columns={[
            {
              title: 'Beschreibung',
              dataIndex: 'description',
              key: 'description',
            },
            {
              title: 'Preis',
              dataIndex: 'price',
              key: 'price',
              render: (price) => `${price} €`,
            },
            {
              title: 'MwSt',
              dataIndex: 'vat',
              key: 'vat',
              render: (vat) => `${vat}%`,
            },
            {
              title: 'Pro Tag',
              dataIndex: 'perDay',
              key: 'perDay',
              render: (perDay) => perDay ? 'Ja' : 'Nein',
            },
            {
              title: 'Optional',
              dataIndex: 'optional',
              key: 'optional',
              render: (optional) => optional ? 'Ja' : 'Nein',
            },
          ]}
        />
        <Button type="dashed" style={{ marginTop: 16, width: '100%' }}>
          + Zusatzpreis hinzufügen
        </Button>
      </Card>

      <Title level={4}>Preisperioden</Title>
      <Card style={{ marginBottom: 16 }}>
        <Table
          dataSource={advert.bookingpriceperiod || []}
          rowKey="uid"
          pagination={false}
          columns={[
            {
              title: 'Titel',
              dataIndex: 'title',
              key: 'title',
            },
            {
              title: 'Zeitraum',
              key: 'period',
              render: (_, record) => (
                <span>
                  {record.dateFrom} - {record.dateTo}
                </span>
              ),
            },
            {
              title: 'Priorität',
              dataIndex: 'priority',
              key: 'priority',
              render: (priority) => {
                const colors = ['default', 'warning', 'error'];
                return <Tag color={colors[priority] || 'default'}>{priority}</Tag>;
              },
            },
            {
              title: 'Preis (1 Person)',
              key: 'price',
              render: (_, record) => record.price?.forOnePerson ? `${record.price.forOnePerson} €` : '-',
            },
          ]}
        />
        <Button type="dashed" style={{ marginTop: 16, width: '100%' }}>
          + Preisperiode hinzufügen
        </Button>
      </Card>

      <Title level={4}>Steuereinstellungen</Title>
      <Card>
        <Table
          dataSource={advert.taxesSettings || []}
          rowKey="uid"
          pagination={false}
          columns={[
            {
              title: 'Titel',
              dataIndex: 'title',
              key: 'title',
            },
            {
              title: 'Zeitraum',
              key: 'period',
              render: (_, record) => (
                <span>
                  {record.dateFrom} - {record.dateTo}
                </span>
              ),
            },
            {
              title: 'Pro Person',
              key: 'perPerson',
              render: (_, record) => record.price?.forOnePerson ? `${record.price.forOnePerson} €` : '-',
            },
            {
              title: 'Pro Hund',
              key: 'perDog',
              render: (_, record) => record.price?.perDog ? `${record.price.perDog} €` : '-',
            },
          ]}
        />
        <Button type="dashed" style={{ marginTop: 16, width: '100%' }}>
          + Steuereinstellung hinzufügen
        </Button>
      </Card>
    </div>
  );
};

// Tab-Komponente für Entfernungen & Lage
const EntfernungenTab: React.FC<{ advert: IAdvert; form: any }> = ({ advert, form }) => {
  return (
    <div>
      <Title level={4}>Entfernungen zu wichtigen Punkten</Title>
      <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtTrainStation']} 
              label="Bahnhof"
            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtAirport']} 
              label="Flughafen"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtBus']} 
              label="Bushaltestelle"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtCityCenter']} 
              label="Stadtzentrum"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtShopping']} 
              label="Einkaufsmöglichkeiten"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtDoctor']} 
              label="Arzt/Krankenhaus"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name={['distances', 'dtBakery']} 
              label="Bäckerei"

            >
              <InputNumber 
                min={0} 
                addonAfter="km" 
                style={{ width: '100%' }}
                placeholder="Entfernung in km"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Divider />

      <Title level={4}>Wegbeschreibung erweitert</Title>
      <Card>
        <Form.Item 
          name="directions_extended" 
          label="Detaillierte Anfahrtsbeschreibung"
          extra="Ergänzende Informationen zur Anreise, Parkmöglichkeiten, etc."
        >
          <TextArea 
            rows={6} 
            placeholder="Detaillierte Wegbeschreibung mit Hinweisen zu Parkmöglichkeiten, öffentlichen Verkehrsmitteln, etc."
          />
        </Form.Item>
      </Card>
    </div>
  );
};

// Tab-Komponente für Zusatzoptionen
const ZusatzoptionenTab: React.FC<{ advert: IAdvert; form: any }> = ({ advert, form }) => {
  return (
    <div>
      <Title level={4}>Bewertung & Status</Title>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item name="star" label="Sterne-Bewertung">
              <Select>
                <Select.Option value={0}>Keine Sterne</Select.Option>
                <Select.Option value={1}>⭐ 1 Stern</Select.Option>
                <Select.Option value={2}>⭐⭐ 2 Sterne</Select.Option>
                <Select.Option value={3}>⭐⭐⭐ 3 Sterne</Select.Option>
                <Select.Option value={4}>⭐⭐⭐⭐ 4 Sterne</Select.Option>
                <Select.Option value={5}>⭐⭐⭐⭐⭐ 5 Sterne</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="cleaningStatus" label="Reinigungsstatus">
              <Select>
                <Select.Option value="">Kein Status</Select.Option>
                <Select.Option value="pending">Ausstehend</Select.Option>
                <Select.Option value="in_progress">In Bearbeitung</Select.Option>
                <Select.Option value="completed">Abgeschlossen</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Title level={4}>Zusätzliche Funktionen</Title>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item 
              name="checklist" 
              valuePropName="checked"
            >
              <Checkbox>Checkliste aktiviert</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name="keycardSystem" 
              valuePropName="checked"
            >
              <Checkbox>Schlüsselkarten-System vorhanden</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Title level={4}>Import-Einstellungen</Title>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item name="importBookingid" label="Import Booking ID">
              <Input placeholder="Externe Buchungs-ID" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="importInvoiceid" label="Import Invoice ID">
              <Input placeholder="Externe Rechnungs-ID" disabled />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Title level={4}>Anzahlung</Title>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item 
              name="percentDepositValue" 
              label="Anzahlung in %"
            >
              <InputNumber 
                min={0} 
                max={100} 
                formatter={value => `${value}%`}
                parser={(value) => value ? parseInt(value.replace('%', '')) : 0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item 
              name="percentDepositText" 
              label="Anzahlungstext"
            >
              <TextArea 
                rows={3} 
                placeholder="Text für die Anzahlung in Angeboten/Rechnungen"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>



      <Title level={4}>Separater Rechnungskreis</Title>
      <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Form.Item 
              name="seperatetInvoiceNumber" 
              valuePropName="checked"
            >
              <Checkbox>Separaten Rechnungsnummernkreis verwenden</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item 
              name="seperatetInvoiceNumberPrefix" 
              label="Präfix für Rechnungsnummer"
            >
              <Input placeholder="z.B. FH-" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </div>
  );
}; 